import React from 'react';
import SwCheckbox from '../Common/FormComponent/SwCheckbox';
import SwBadge from '../Common/SwBadge/SwBadge';
import SwLoader from '../Common/SwLoader/SwLoader';

/**
 * Per-device selection for an Extended Hours booking (client SPOC).
 *
 * Mirrors the ops booking flow: ACs are CHARGEABLE (multi-select, per-hour rate,
 * Manual/Cloud control shown) and Lights are FREE. Selecting nothing books the whole
 * floor as before; a lights-only selection is a valid ₹0 booking.
 *
 * `acs`    : [{ acId, label, typeLabel, ratePerHour, controlType }]
 * `lights` : [{ deviceUniqueId, name, zoneName }]
 */
const rupee = (n) =>
  n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN')}`;

const DevicePicker = ({
  acs = [],
  lights = [],
  selectedAcIds = [],
  selectedLightIds = [],
  onChangeAcIds,
  onChangeLightIds,
  billableHours = 1,
  loading = false,
  billingPrinciple = null,   // 'ac-based' | 'flat' | 'submeter'
  flatRateForDay = null,     // flat rate/hour for the booking's day (flat billing)
  blockedAcIds = null,       // Set of ac.acId reserved by an overlapping booking → disabled
  blockedLightIds = null,    // Set of deviceUniqueId reserved by an overlapping booking → disabled
  blockAll = false,          // whole-scope conflict (flat floor / whole-floor booking) → disable all
  blockedWindowById = null,  // Map<deviceId, "6:00 PM–7:00 PM"> for the disabled-reason subtext
}) => {
  const acSet = new Set(selectedAcIds);
  const lightSet = new Set(selectedLightIds);
  const isFlat = billingPrinciple === 'flat';
  const isSubmeter = billingPrinciple === 'submeter';
  // flat/submeter never bill per AC — hide per-AC ₹ and the AC-based estimate.
  const hideAcPricing = isFlat || isSubmeter;

  const acBlocked = (id) => blockAll || (blockedAcIds && blockedAcIds.has(id));
  const lightBlocked = (id) => blockAll || (blockedLightIds && blockedLightIds.has(id));

  const toggleAc = (id) => {
    if (acBlocked(id)) return;
    const next = new Set(acSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChangeAcIds(Array.from(next));
  };

  const toggleLight = (id) => {
    if (lightBlocked(id)) return;
    const next = new Set(lightSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChangeLightIds(Array.from(next));
  };

  // Drop any selected device that an overlapping booking now blocks (e.g. after the user
  // changed the time). On a whole-scope/flat conflict, clear the whole selection. Only the
  // open modal's DevicePicker is mounted, so this acts on the correct window.
  React.useEffect(() => {
    if (blockAll) {
      if (selectedAcIds.length) onChangeAcIds([]);
      if (selectedLightIds.length) onChangeLightIds([]);
      return;
    }
    if (blockedAcIds && selectedAcIds.some((id) => blockedAcIds.has(id))) {
      onChangeAcIds(selectedAcIds.filter((id) => !blockedAcIds.has(id)));
    }
    if (blockedLightIds && selectedLightIds.some((id) => blockedLightIds.has(id))) {
      onChangeLightIds(selectedLightIds.filter((id) => !blockedLightIds.has(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockAll, blockedAcIds, blockedLightIds]);

  // Live gross estimate, by billing principle:
  //   submeter → null (billed by meter reading);
  //   flat → flat rate for the day × hours (independent of AC selection);
  //   ac-based → Σ(selected AC rates) × billable hours (min 1), null if any rate unknown.
  let estimate;
  if (isSubmeter) {
    estimate = null;
  } else if (isFlat) {
    estimate =
      flatRateForDay === null || flatRateForDay === undefined
        ? null
        : Number(flatRateForDay) * Math.max(1, billableHours);
  } else {
    estimate = 0;
    let rateUnknown = false;
    acs.forEach((ac) => {
      if (!acSet.has(ac.acId)) return;
      if (ac.ratePerHour === null || ac.ratePerHour === undefined) rateUnknown = true;
      else estimate += Number(ac.ratePerHour);
    });
    estimate = rateUnknown ? null : estimate * Math.max(1, billableHours);
  }

  if (loading) {
    return (
      <div className="DevicePicker mt-3">
        <SwLoader />
      </div>
    );
  }

  return (
    <div className="DevicePicker mt-3">
      <h3 className="SwModalSubHeading">Select devices to run</h3>
      <p className="SwTextSmall SwTextGray mb-2">
        Pick the exact ACs and lights for this booking. Leave everything unselected to
        book the whole floor. Lights are always free.
      </p>

      {blockAll && (
        <p className="SwTextSmall SwTextDanger mb-2">
          All devices are already booked for this time window — pick a different time.
        </p>
      )}

      <div className="row">
        {/* ── ACs — chargeable ─────────────────────────────────────── */}
        <div className="col-md-6">
          <div className="DevicePicker-Col">
            <div className="DevicePicker-ColHead">
              <span className="fw-600">Air-conditioners</span>
              <span className="SwTextSmall SwTextGray">
                {isSubmeter ? 'metered' : isFlat ? 'flat rate' : 'chargeable'}
              </span>
            </div>

            {acs.length === 0 ? (
              <p className="SwTextSmall SwTextGray mb-0 p-2">
                No ACs on record for this floor — lights-only booking.
              </p>
            ) : (
              <div className="DevicePicker-List">
                {acs.map((ac) => {
                  const blocked = acBlocked(ac.acId);
                  const bookedWindow = blockedWindowById && blockedWindowById.get(ac.acId);
                  return (
                  <div className={`DevicePicker-Row${blocked ? ' is-disabled' : ''}`} key={ac.acId}>
                    {/* Show the human AC note as the primary label (understood by
                        the on-ground team, editable) and the machine AC code beneath
                        it. Falls back to the code when no note is set. */}
                    <SwCheckbox
                      id={`ac-${ac.acId}`}
                      checked={acSet.has(ac.acId)}
                      onChange={() => toggleAc(ac.acId)}
                      disabled={blocked}
                      label={
                        <span className="DevicePicker-AcLabel">
                          <span className="DevicePicker-AcNote">{ac.note || ac.acCode || ac.label}</span>
                          {ac.note && ac.acCode ? (
                            <span className="DevicePicker-AcCode SwTextGray">{ac.acCode}</span>
                          ) : null}
                        </span>
                      }
                    />
                    <div className="DevicePicker-RowMeta">
                      {blocked ? (
                        <span className="SwTextSmall SwTextDanger">Booked{bookedWindow ? ` · ${bookedWindow}` : ''}</span>
                      ) : (
                        <span className="SwTextSmall SwTextGray">{ac.typeLabel}</span>
                      )}
                      <SwBadge
                        variant={ac.controlType === 'CLOUD' ? 'success' : 'info'}
                        text={ac.controlType === 'CLOUD' ? 'Cloud' : 'Manual'}
                      />
                      {!hideAcPricing && (
                        <span className="SwTextSmall">{rupee(ac.ratePerHour)}/hr</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Lights — free ────────────────────────────────────────── */}
        <div className="col-md-6">
          <div className="DevicePicker-Col">
            <div className="DevicePicker-ColHead">
              <span className="fw-600">Lights</span>
              <span className="SwTextSmall SwTextGray">free</span>
            </div>

            {lights.length === 0 ? (
              <p className="SwTextSmall SwTextGray mb-0 p-2">
                No lights mapped for this floor.
              </p>
            ) : (
              <div className="DevicePicker-List">
                {lights.map((l) => {
                  const blocked = lightBlocked(l.deviceUniqueId);
                  const bookedWindow = blockedWindowById && blockedWindowById.get(l.deviceUniqueId);
                  return (
                  <div className={`DevicePicker-Row${blocked ? ' is-disabled' : ''}`} key={l.deviceUniqueId}>
                    <SwCheckbox
                      id={`light-${l.deviceUniqueId}`}
                      checked={lightSet.has(l.deviceUniqueId)}
                      onChange={() => toggleLight(l.deviceUniqueId)}
                      disabled={blocked}
                      label={l.name || l.deviceUniqueId}
                    />
                    <div className="DevicePicker-RowMeta">
                      {blocked ? (
                        <span className="SwTextSmall SwTextDanger">Booked{bookedWindow ? ` · ${bookedWindow}` : ''}</span>
                      ) : (
                        <span className="SwTextSmall SwTextGray">{l.zoneName}</span>
                      )}
                      <span className="SwTextSmall SwTextSuccess">Free</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Running total */}
      <div className="DevicePicker-Total">
        <span className="SwTextSmall SwTextGray">
          Selected: {acSet.size} AC{acSet.size === 1 ? '' : 's'} · {lightSet.size} light
          {lightSet.size === 1 ? '' : 's'}
        </span>
        <span className="fw-600">
          {isSubmeter
            ? 'Billed by meter'
            : isFlat
              ? `Flat: ${flatRateForDay === null || flatRateForDay === undefined ? '—' : `${rupee(flatRateForDay)}/hr`}${estimate !== null ? ` · Est. ${rupee(estimate)}` : ''}`
              : `Est. ${estimate === null ? 'unavailable' : rupee(estimate)}`}
        </span>
      </div>
      <p className="SwTextSmall SwTextGray mb-0">
        {isSubmeter
          ? 'Sub-meter billing — invoiced from your meter reading for the booking window, not per AC.'
          : isFlat
            ? "Flat-rate billing — charged at the day's flat rate for the booking window, not per AC. Lights are free."
            : 'Estimate is for the selected ACs only — the final invoice applies your free quota.'}
      </p>
    </div>
  );
};

export default DevicePicker;
